import { useMemo, useRef, useState, type PointerEvent } from 'react'
import { useReactFlow, type ReactFlowInstance } from '@xyflow/react'
import getStroke from 'perfect-freehand'

export type FreehandPoint = [number, number, number]
export type FreehandPoints = FreehandPoint[]

export interface FloorPlanFreehandStyle {
  kind: 'freehand'
  points: FreehandPoints
  initialSize: {
    width: number
    height: number
  }
  width: number
  height: number
  stroke: string
  strokeWidth: number
}

export interface ProcessedFreehandDrawing {
  position: {
    x: number
    y: number
  }
  properties: FloorPlanFreehandStyle
}

const DEFAULT_FREEHAND_STROKE = '#7c3aed'
const DEFAULT_FREEHAND_STROKE_WIDTH = 7

const pathOptions = {
  size: DEFAULT_FREEHAND_STROKE_WIDTH,
  thinning: 0.5,
  smoothing: 0.5,
  streamline: 0.5,
  easing: (t: number) => t,
  start: {
    taper: 0,
    easing: (t: number) => t,
    cap: true,
  },
  end: {
    taper: 0.1,
    easing: (t: number) => t,
    cap: true,
  },
}

function getSvgPathFromStroke(stroke: number[][]): string {
  if (!stroke.length) return ''

  const d = stroke.reduce(
    (acc, [x0, y0], i, arr) => {
      const [x1, y1] = arr[(i + 1) % arr.length]
      acc.push(x0, y0, ',', (x0 + x1) / 2, (y0 + y1) / 2)
      return acc
    },
    ['M', ...stroke[0], 'Q'] as Array<string | number>,
  )

  d.push('Z')
  return d.join(' ')
}

export function freehandPointsToPath(points: FreehandPoints, zoom = 1, strokeWidth = DEFAULT_FREEHAND_STROKE_WIDTH): string {
  const stroke = getStroke(points, {
    ...pathOptions,
    size: strokeWidth * zoom,
  })

  return getSvgPathFromStroke(stroke)
}

export function normalizeFreehandStyle(raw: unknown): FloorPlanFreehandStyle {
  const value = raw && typeof raw === 'object' ? raw as Partial<FloorPlanFreehandStyle> : {}
  const initialWidth = Math.max(1, Number(value.initialSize?.width) || Number(value.width) || 1)
  const initialHeight = Math.max(1, Number(value.initialSize?.height) || Number(value.height) || 1)
  const width = Math.max(1, Number(value.width) || initialWidth)
  const height = Math.max(1, Number(value.height) || initialHeight)

  return {
    kind: 'freehand',
    points: Array.isArray(value.points) ? value.points as FreehandPoints : [],
    initialSize: {
      width: initialWidth,
      height: initialHeight,
    },
    width,
    height,
    stroke: typeof value.stroke === 'string' ? value.stroke : DEFAULT_FREEHAND_STROKE,
    strokeWidth: Math.max(1, Number(value.strokeWidth) || DEFAULT_FREEHAND_STROKE_WIDTH),
  }
}

function processFreehandPoints(
  points: FreehandPoints,
  screenToFlowPosition: ReactFlowInstance['screenToFlowPosition'],
  stroke: string,
  strokeWidth: number,
): ProcessedFreehandDrawing | null {
  if (points.length < 2) return null

  let x1 = Infinity
  let y1 = Infinity
  let x2 = -Infinity
  let y2 = -Infinity
  const flowPoints: FreehandPoints = []

  for (const point of points) {
    const { x, y } = screenToFlowPosition({ x: point[0], y: point[1] })
    x1 = Math.min(x1, x)
    y1 = Math.min(y1, y)
    x2 = Math.max(x2, x)
    y2 = Math.max(y2, y)
    flowPoints.push([x, y, point[2]])
  }

  const thickness = strokeWidth * 0.5
  x1 -= thickness
  y1 -= thickness
  x2 += thickness
  y2 += thickness

  for (const point of flowPoints) {
    point[0] -= x1
    point[1] -= y1
  }

  const width = Math.max(1, x2 - x1)
  const height = Math.max(1, y2 - y1)

  return {
    position: { x: x1, y: y1 },
    properties: {
      kind: 'freehand',
      points: flowPoints,
      initialSize: { width, height },
      width,
      height,
      stroke,
      strokeWidth,
    },
  }
}

export function FreehandSvg({
  properties,
}: {
  properties: FloorPlanFreehandStyle
}) {
  const scaleX = properties.width / properties.initialSize.width
  const scaleY = properties.height / properties.initialSize.height
  const points = useMemo(
    () => properties.points.map((point) => [point[0] * scaleX, point[1] * scaleY, point[2]] as FreehandPoint),
    [properties.points, scaleX, scaleY],
  )

  return (
    <svg width="100%" height="100%" viewBox={`0 0 ${properties.width} ${properties.height}`} className="overflow-visible">
      <path
        d={freehandPointsToPath(points, 1, properties.strokeWidth)}
        fill={properties.stroke}
        style={{
          pointerEvents: 'visiblePainted',
          cursor: 'pointer',
        }}
      />
    </svg>
  )
}

export function FreehandOverlay({
  onComplete,
  stroke = DEFAULT_FREEHAND_STROKE,
  strokeWidth = DEFAULT_FREEHAND_STROKE_WIDTH,
}: {
  onComplete: (drawing: ProcessedFreehandDrawing) => void
  stroke?: string
  strokeWidth?: number
}) {
  const { screenToFlowPosition, getViewport } = useReactFlow()
  const screenPointsRef = useRef<FreehandPoints>([])
  const previewPointsRef = useRef<FreehandPoints>([])
  const [previewPoints, setPreviewPoints] = useState<FreehandPoints>([])

  const appendPoint = (event: PointerEvent<HTMLDivElement>) => {
    const rect = event.currentTarget.getBoundingClientRect()
    const pressure = event.pressure > 0 ? event.pressure : 0.5
    const screenPoint: FreehandPoint = [event.clientX, event.clientY, pressure]
    const previewPoint: FreehandPoint = [event.clientX - rect.left, event.clientY - rect.top, pressure]

    screenPointsRef.current = [...screenPointsRef.current, screenPoint]
    previewPointsRef.current = [...previewPointsRef.current, previewPoint]
    setPreviewPoints(previewPointsRef.current)
  }

  const reset = () => {
    screenPointsRef.current = []
    previewPointsRef.current = []
    setPreviewPoints([])
  }

  const handlePointerDown = (event: PointerEvent<HTMLDivElement>) => {
    event.preventDefault()
    event.currentTarget.setPointerCapture(event.pointerId)
    reset()
    appendPoint(event)
  }

  const handlePointerMove = (event: PointerEvent<HTMLDivElement>) => {
    if (screenPointsRef.current.length === 0 || event.buttons !== 1) return
    appendPoint(event)
  }

  const handlePointerUp = (event: PointerEvent<HTMLDivElement>) => {
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId)
    }

    const drawing = processFreehandPoints(screenPointsRef.current, screenToFlowPosition, stroke, strokeWidth)
    reset()
    if (drawing) onComplete(drawing)
  }

  return (
    <div
      className="floor-plan-freehand-overlay"
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={reset}
    >
      <svg>
        <path d={freehandPointsToPath(previewPoints, getViewport().zoom, strokeWidth)} fill={stroke} />
      </svg>
    </div>
  )
}
